import { message, Radio, Switch, UploadFile } from "antd";
import type { RcFile } from "antd/es/upload";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import AppHeader from "../../../../components/AppHeader/AppHeader";
import AddOrCreateScriptCategory from "../../../../components/chatScripts/AddOrCreateScriptCategory/AddOrCreateScriptCategory";
import ChatScriptFilesUpload from "../../../../components/chatScripts/ChatScriptFilesUpload/ChatScriptFilesUpload";
import Loader from "../../../../components/loader/Loader";
import MessageScriptTextArea from "../../../../components/MessageScriptTextArea/MessageScriptTextArea";
import PriceInput from "../../../../components/PriceInputComponent/PriceInputComponent";
import SpinnerComponent from "../../../../components/SpinnerComponent/SpinnerComponent";
import StickyButton from "../../../../components/StickyButtonComponent/StickyButtonComponent";
import { getDiscountExpirePeriods, getMediaAmounts, MAX_MEDIAS_TO_SEND, MAX_PRICE } from "../../../../helpers/constant";
import { FilePrivacy } from "../../../../helpers/types";
import useFetchUserInfos from "../../../../hooks/userHooks/useFetchUserInfos";
import { clearScriptsCache } from "../../../../redux/chatScript/slices/scriptSlice";
import { fetchScriptsByFilter } from "../../../../redux/chatScript/thunks/scriptThunks";
import AudioIcon from "../../../../assets/icons/white-audio.svg";
import { useAppDispatch } from "../../../../redux/hooks";
import { chatScriptService } from "../../../../services/chat-script.service";
import { storageService } from "../../../../services/storage.service";
import { socket } from "../../../../socket";
import { generateThumbnailURL, generateUniqueFileName, generateURL } from "../../../../utls/FunctionsUtil";
import "./ChatScriptsCreateView.less";

const ChatScriptsCreateView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { currentUser } = useFetchUserInfos();
  const currentCreator = currentUser?.id;
  const [step, setStep] = useState<1 | 2>(1);
  const [title, setTitle] = useState("");
  const [files, setFiles] = useState<any[]>([]);
  type PrivateType = { showDialog: boolean; fileId: string; fileSrc: any; fileType: string };
  const [privateFiles, setPrivateFiles] = useState<PrivateType[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
  const [messageText, setMessageText] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const mediaAmounts = getMediaAmounts(t);
  const discountExpirePeriods = getDiscountExpirePeriods(t);
  const firstThreePeriods = discountExpirePeriods.slice(0, 3);
  const lastThreePeriods = discountExpirePeriods.slice(3);
  const [radioValue, setRadioValue] = useState<string | undefined>(undefined);
  const [customPrice, setCustomPrice] = useState<number | undefined>(undefined);
  const [promoCodeChecked, setPromoCodeChecked] = useState(false);
  const [discountCurrency, setDiscountCurrency] = useState<string | undefined>(undefined);
  const [discountPercentage, setDiscountPercentage] = useState<string | undefined>(undefined);
  const [selectedDiscountPeriod, setSelectedDiscountPeriod] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const [progress, setProgress] = useState<number>(0);
  const [loadedData, setLoadedData] = useState<number>(0);
  const [totalSize, setTotalSize] = useState<number>(0);

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const promoContentRef = useRef<HTMLDivElement>(null);
  const noFiles = useMemo(() => !(Array.isArray(files) && files.length > 0), [files]);
  const isContinueDisabled = useMemo(() => title.trim().length === 0, [title]);
  const isConfirmDisabled = useMemo(() => {
    const hasMessage = !!messageText && messageText.trim() !== "";
    const hasFiles = Array.isArray(files) && files.length > 0;
    const hasExplicitPrice = customPrice !== undefined || (radioValue !== undefined && radioValue !== "");
    const promoNeedsValue = promoCodeChecked
      ? (!!discountCurrency && Number(discountCurrency) > 0) || (!!discountPercentage && Number(discountPercentage) > 0)
      : true;
    const promoNeedsDate = promoCodeChecked ? selectedDiscountPeriod !== undefined : true;

    if (!hasMessage) return true;
    if (hasFiles && !hasExplicitPrice) return true;
    if (!promoNeedsValue || !promoNeedsDate) return true;
    return false;
  }, [messageText, files, customPrice, radioValue, promoCodeChecked, discountCurrency, discountPercentage, selectedDiscountPeriod]);

  useEffect(() => {
    if (totalSize > 0) {
      const uploadProgress = Math.round((loadedData / totalSize) * 100);
      const globalProgress = Math.round(uploadProgress / 2);
      setProgress((prev) => {
        if (globalProgress > prev) {
          return globalProgress;
        }
        return prev;
      });
    }
  }, [loadedData]);

  useEffect(() => {
    function onProgressUpdate(data: number) {
      setProgress((prev) => {
        const prog = prev + 1;
        if (prog <= 99) {
          return prog;
        }
        return prev;
      });
    }

    function onProcessEnd(data: any) {
      setLoading(false);
      message.success(t("script-created-successfully"));
      navigate("/");
    }
    socket.on("file-updated", onProcessEnd);
    socket.on("progress-update", onProgressUpdate);
    return () => {
      socket.off("file-updated", onProcessEnd);
      socket.off("progress-update", onProgressUpdate);
    };
  }, []);

  // Force price to be free when there are no files
  useEffect(() => {
    if (files.length === 0) {
      const freeOption = mediaAmounts.find((opt) => String(opt.value) === "0" || parseInt(String(opt.value), 10) === 0);
      setCustomPrice(undefined);
      setRadioValue(freeOption ? String(freeOption.value) : "0");
      setPromoCodeChecked(false);
    }
  }, [files.length, mediaAmounts]);

  const handleContinue = () => setStep(2);

  const handleConfirm = async () => {
    if (selectedCategoryId === "") {
      message.error(t("message-select-category"));
      return;
    }

    setLoading(true);
    try {
      const filesPrivacy: Array<FilePrivacy & { originalFileName?: string }> = [];
      const uniqueQuickFiles: any[] = (files || [])
        .filter((f: any) => f.imgType === "quick-file")
        .map((f: any) => ({ id: f.uid, isPrivate: !!f.isPrivate }));

      const discountValue = discountCurrency ?? discountPercentage;
      const discountType = discountCurrency ? "CURRENCY" : discountPercentage ? "PERCENTAGE" : undefined;
      const rawExpireType = selectedDiscountPeriod !== undefined ? discountExpirePeriods[selectedDiscountPeriod].value.timeType : undefined;
      const expireTimeType = rawExpireType === "HOURS" ? "HOURS" : (rawExpireType as "HOURS" | "DAYS" | "UNLIMITED" | undefined);

      const fd = new FormData();
      fd.append("creatorId", currentCreator || "");
      fd.append("title", title ? title.trim() : "");
      fd.append("categoryId", selectedCategoryId || "");
      fd.append("message", messageText.trim());
      if (currentCreator) fd.append("createdBy", currentCreator);
      // If there are no files, force price to be 0
      const finalPrice = files.length === 0 ? 0 : customPrice ?? (radioValue != null ? parseInt(radioValue, 10) : 0);
      fd.append("price", String(finalPrice));
      // If there are no files, force promo to be false
      const finalPromo = files.length === 0 ? false : promoCodeChecked;
      fd.append("promo", String(finalPromo));
      if (discountValue) fd.append("discount", String(Number(discountValue)));
      if (discountType) fd.append("discountType", discountType);
      if (selectedDiscountPeriod !== undefined) {
        fd.append("expireTime", String(discountExpirePeriods[selectedDiscountPeriod].value.value));
        if (expireTimeType) fd.append("expireTimeType", expireTimeType);
      }
      if (notes && notes.trim()) fd.append("notes", notes.trim());

      const videoFileToChunk: Array<{ file: any; isPrivate: boolean; name: string; size: number }> = [];
      const fileNameMap = new Map();
      (files || []).forEach((file: any) => {
        if (!(isPlainObject(file) && file?.imgType !== "quick-file")) return;
        const raw = file?.originFileObj as RcFile | undefined;
        if (!raw) return;
        const uniqueFileName = generateUniqueFileName(file.name);
        fileNameMap.set(file.uid, uniqueFileName);
      });

      (files || []).forEach((file: any) => {
        if (file?.imgType !== "quick-file") {
          filesPrivacy.push({
            fileId: file.uid,
            isPrivate: !!file.isPrivate,
            originalFileName: fileNameMap.get(file.uid) || file.name,
          });
        }
      });

      (files || []).forEach((file: any) => {
        if (!(isPlainObject(file) && file?.imgType !== "quick-file")) return;
        const raw = file?.originFileObj as RcFile | undefined;
        if (!raw) return;

        const uniqueFileName = fileNameMap.get(file.uid);

        if (raw.type?.includes("image/")) {
          const newFile = new File([raw], uniqueFileName, { type: raw.type });
          fd.append("files", newFile);
        } else if (raw.type?.includes("audio/")) {
          const newFile = new File([raw], uniqueFileName, { type: raw.type });
          fd.append("files", newFile);
        } else if (raw.type?.includes("video/")) {
          videoFileToChunk.push({ file, isPrivate: !!file.isPrivate, name: file.name, size: file.size });
        }
      });

      if (videoFileToChunk.length > 0) {
        const totalSizes = videoFileToChunk.reduce((acc: number, f) => acc + f.size, 0);
        setTotalSize(totalSizes);
        const videoFiles: any[] = [];
        for (let i = 0; i < videoFileToChunk.length; i++) {
          const vf = videoFileToChunk[i];
          const result = await storageService.uploadFileMultiPart(vf.file, setLoadedData, totalSize);
          videoFiles.push({
            Location: result.Location,
            isPrivate: vf.isPrivate,
            Key: result.Key,
            ETag: result.ETag,
            Bucket: result.Bucket,
            filename: vf.name,
            size: vf.size,
          });
        }
        fd.append("videoFiles", JSON.stringify(videoFiles));
      }

      fd.append("fileIds", JSON.stringify(filesPrivacy));
      fd.append("quickFiles", JSON.stringify(uniqueQuickFiles));

      await chatScriptService.createScriptV2(fd as any, setProgress).then((res: any) => {
        if (res) {
          message.success(t("script-created-successfully"));
          window.localStorage.removeItem("chatScriptsCreate.keep");
        }
      });
      try {
        dispatch(clearScriptsCache());
        if (currentCreator) {
          await (dispatch as any)(fetchScriptsByFilter({ creatorId: currentCreator, filterOrCategoryId: "all", page: 1 }));
        }
      } catch {}
      const params = new URLSearchParams(location.search);
      const conversationId = params.get("conversationId");
      if (conversationId) {
        navigate(`/creator/conversation/${conversationId}`);
      } else {
        navigate("/creator/chat-scripts");
      }
    } catch (e) {
      console.error(e);
      message.error(t("back-error-message"));
    } finally {
      setLoading(false);
    }
  };

  const handleCustomPriceChange = (value: number | null) => {
    if (noFiles) return;
    if (value !== null) {
      setCustomPrice(value);
      setRadioValue(undefined);
      if (value > MAX_PRICE) message.warning(t("maximum-price-alert"));
    }
  };

  const handleRadioChange = (e: any) => {
    if (noFiles) return;
    const value = e.target.value;
    setRadioValue(value);
    setCustomPrice(parseInt(value));
    if (value > MAX_PRICE) message.warning(t("maximum-price-alert"));
  };

  const handlePromoToggle = (checked: boolean) => {
    setPromoCodeChecked(checked);
    if (checked) {
      setTimeout(() => {
        promoContentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    if (e.ctrlKey || e.metaKey || key === "Backspace" || key === "Tab" || key === "ArrowLeft" || key === "ArrowRight" || key === "Delete") return;
    if (key < "0" || key > "9") e.preventDefault();
  };

  const handleDiscountCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDiscountCurrency(value);
    setDiscountPercentage(undefined);
  };

  const handleDiscountPercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = parseInt(e.target.value);
    if (!isNaN(value)) {
      if (value > 100) value = 100;
      else if (value < 0) value = 0;
    } else {
      value = 0;
    }
    setDiscountPercentage(value.toString());
    setDiscountCurrency(undefined);
  };

  function isPlainObject(value: any) {
    if (value === null || typeof value !== "object") return false;
    return Object.getPrototypeOf(value) === Object.prototype;
  }

  const handleExpireTimeChange = (_period: { value: string; timeType: string }, index: number) => {
    setSelectedDiscountPeriod(index);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const selected = params.get("selected");
    const keep = window.localStorage.getItem("chatScriptsCreate.keep") === "1";

    if (selected || keep) {
      if (title === "") {
        const storedTitle = window.localStorage.getItem("chatScriptsCreate.title");
        if (storedTitle) setTitle(storedTitle);
      }
      if (step !== 2) setStep(2);
      if (keep) window.localStorage.removeItem("chatScriptsCreate.keep");
    } else {
      window.localStorage.removeItem("chatScriptsCreate.title");
      if (title !== "") setTitle("");
      if (step !== 1) setStep(1);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("chatScriptsCreate.title", title);
  }, [title]);

  useEffect(() => {
    return () => {
      const keep = window.localStorage.getItem("chatScriptsCreate.keep") === "1";
      if (!keep) window.localStorage.removeItem("chatScriptsCreate.title");
    };
  }, []);

  const onFilesChange = async (newList: UploadFile<[]>[] = []) => {
    const currentFiles = files || [];
    const existingIds = new Set(currentFiles.map((f: any) => f.uid));
    const newIds = new Set(newList.map((f: any) => f.uid));

    let hasChanged = currentFiles.length !== newList.length;

    if (!hasChanged) {
      const existingIdsArray = Array.from(existingIds);
      const newIdsArray = Array.from(newIds);
      for (const id of existingIdsArray) {
        if (!newIds.has(id)) {
          hasChanged = true;
          break;
        }
      }
      if (!hasChanged) {
        for (const id of newIdsArray) {
          if (!existingIds.has(id)) {
            hasChanged = true;
            break;
          }
        }
      }
    }

    const added = newList.filter((f: any) => !existingIds.has(f.uid) && f.status === "done");

    if (added.length > 0) {
      const filesTSP: PrivateType[] = await Promise.all(
        added.map(async (file: any) => {
          let src = file.url as string;
          if (file.imgType !== "quick-file") {
            if (file.type?.includes("video")) {
              src = await generateThumbnailURL(file.originFileObj as RcFile);
            } else {
              src = await generateURL(file.originFileObj as RcFile);
            }
          } else {
            src = file.thumbUrl!;
          }
          return { showDialog: true, fileId: file.uid, fileSrc: src, fileType: file.type! };
        })
      );
      const seen = new Set<string>();
      const uniq = [...privateFiles, ...filesTSP].filter((f) => {
        if (seen.has(f.fileId)) return false;
        seen.add(f.fileId);
        return true;
      });
      setPrivateFiles(uniq);

      if (privateFiles.length === 0) {
        setCurrentFileIndex(0);
      }
    }

    // Check if all files were removed
    if (newList.length === 0 && files.length > 0) {
      // Reset price to 0 when all files are removed
      const freeOption = mediaAmounts.find((opt) => String(opt.value) === "0" || parseInt(String(opt.value), 10) === 0);
      setCustomPrice(undefined);
      setRadioValue(freeOption ? String(freeOption.value) : "0");

      // Also disable promo since it doesn't make sense for a free script
      setPromoCodeChecked(false);
    }

    setFiles(newList || []);
  };

  // Reset price and promo when files are cleared
  useEffect(() => {
    if (!files || files.length === 0) {
      if (customPrice !== undefined) setCustomPrice(undefined);
      if (radioValue !== undefined) setRadioValue(undefined);
      if (promoCodeChecked) setPromoCodeChecked(false);
      if (discountCurrency !== undefined) setDiscountCurrency(undefined);
      if (discountPercentage !== undefined) setDiscountPercentage(undefined);
      if (selectedDiscountPeriod !== undefined) setSelectedDiscountPeriod(undefined);
    }
  }, [files]);

  const onNextClick = (privateFile: PrivateType) => {
    setPrivateFiles((prev) => prev.filter((f) => f.fileId !== privateFile.fileId));
    setCurrentFileIndex((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const handleLockSwitchChange = (checked: boolean, privateFile: PrivateType) => {
    setFiles((prev: any[]) =>
      (prev || []).map((file: any) => {
        if (file.uid === privateFile.fileId) {
          return { ...file, isPrivate: checked };
        }
        // Ensure all other files have isPrivate set to true by default if not already set
        return { ...file, isPrivate: file.isPrivate !== undefined ? file.isPrivate : true };
      })
    );
  };

  const handleRemoveFile = (fileId: string) => {
    setPrivateFiles((prev) => prev.filter((f) => f.fileId !== fileId));
    const updatedFiles = (files || []).filter((f: any) => f.uid !== fileId);
    setFiles(updatedFiles);
    removeFileFromSelected(fileId);
  };

  const removeFileFromSelected = (fileId: string) => {
    const params = new URLSearchParams(location.search);
    const selectedParam = params.get("selected");
    let selectedFiles: string[] = selectedParam ? JSON.parse(selectedParam) : [];
    selectedFiles = selectedFiles.filter((id) => id !== fileId);
    if (selectedFiles.length > 0) {
      params.set("selected", JSON.stringify(selectedFiles));
    } else {
      params.delete("selected");
    }
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  return (
    <div className="chat-scripts-create-view">
      {privateFiles.length === 0 && (
        <AppHeader
          title={t("create-a-script")}
          backButtonDestination={-1}
          showBackButton={true}
          onBackClick={step === 2 ? () => setStep(1) : undefined}
        />
      )}

      <div className="chat-scripts-create-content" style={{ minHeight: privateFiles.length > 0 ? "100vh" : "auto" }}>
        {step === 1 ? (
          <div className="step step-title create-form">
            <div className="centered-block">
              <div className="form-container">
                <h2 className="chat-script-create-form-title">{t("give-your-script-a-name")}</h2>
                <div className="chat-script-create-input-group">
                  <input
                    type="text"
                    className="chat-script-create-title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t("my-name") as string}
                    autoFocus
                  />
                  <div className="chat-script-create-input-underline" />
                </div>
              </div>
            </div>
            <div className="bottom-button-wrapper">
              <button className="chat-script-create-button chat-script-create-button--default" onClick={handleContinue} disabled={isContinueDisabled}>
                {t("continue")}
              </button>
            </div>
          </div>
        ) : (
          <div className="chat-scripts-step-2" style={{ display: privateFiles.length > 0 ? "none" : "" }}>
            <div className="chat-scripts-content">
              {/* Media upload section */}
              <div className="media-section">
                <ChatScriptFilesUpload maxCount={MAX_MEDIAS_TO_SEND} onFilesChange={onFilesChange} defaultList={files} />
                <p className="section-description">{t("send-file-description")}</p>
              </div>

              {/* Add or create script category selector */}
              <div className="script-category-section">
                {currentCreator && (
                  <AddOrCreateScriptCategory creatorId={currentCreator} selectedCategoryId={selectedCategoryId} onSelect={setSelectedCategoryId} />
                )}
              </div>

              {/* Message text area */}
              <div className="message-section">
                <MessageScriptTextArea
                  title={t("message")}
                  value={messageText}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessageText(e.target.value)}
                  placeholder={`${t("your-message")}...`}
                />
              </div>

              {/* Price selection */}
              {files.length === 0 ? (
                <></>
              ) : (
                <div className="price-section">
                  <PriceInput customPrice={customPrice} handleCustomPriceChange={handleCustomPriceChange} />

                  <Radio.Group className="price-options-group" value={radioValue} onChange={handleRadioChange}>
                    <div className="price-options-container">
                      {mediaAmounts.map((option, index) => (
                        <Radio.Button key={index} value={option.value} className="price-option-button">
                          {option.label}
                        </Radio.Button>
                      ))}
                    </div>
                  </Radio.Group>
                </div>
              )}

              {/* Promotion section - only show when there are files */}
              {files.length > 0 && (
                <div className="promo-section">
                  <div className="promo-header">
                    <h3 className="promo-title">{t("add-a-promotion")}</h3>
                    <label className="promo-switch">
                      <input
                        type="checkbox"
                        checked={promoCodeChecked}
                        onChange={(e) => handlePromoToggle(e.target.checked)}
                        disabled={!customPrice || customPrice <= 0}
                      />
                      <span className="promo-slider"></span>
                    </label>
                  </div>

                  {promoCodeChecked && (
                    <div className="promo-content" ref={promoContentRef}>
                      <div className="discount-section">
                        <h4 className="discount-title">{t("discount")}</h4>
                        <div className="discount-inputs">
                          <div className={`discount-input-container ${discountCurrency ? "active" : ""}`}>
                            <input
                              type="number"
                              placeholder="0"
                              value={discountCurrency || ""}
                              onChange={handleDiscountCurrencyChange}
                              onKeyDown={handleKeyDown}
                              className="discount-input"
                              inputMode="numeric"
                            />
                            <span className="input-suffix">€</span>
                          </div>

                          <span className="discount-divider">{t("or")}</span>

                          <div className={`discount-input-container ${discountPercentage ? "active" : ""}`}>
                            <input
                              type="number"
                              placeholder="0"
                              value={discountPercentage || ""}
                              onChange={handleDiscountPercentageChange}
                              onKeyDown={handleKeyDown}
                              max="100"
                              className="discount-input"
                              inputMode="numeric"
                            />
                            <span className="input-suffix">%</span>
                          </div>
                        </div>
                      </div>

                      <div className="discount-duration">
                        <h4 className="duration-title">{t("duration-of-validity")}</h4>
                        <div className="duration-options-row">
                          {firstThreePeriods.map((period, index) => (
                            <button
                              key={index}
                              className={`duration-option ${selectedDiscountPeriod === index ? "selected" : ""}`}
                              onClick={() => handleExpireTimeChange(period.value, index)}
                            >
                              {period.label}
                            </button>
                          ))}
                        </div>
                        <div className="duration-options-row">
                          {lastThreePeriods.map((period, index) => (
                            <button
                              key={index + 3}
                              className={`duration-option ${selectedDiscountPeriod === index + 3 ? "selected" : ""}`}
                              onClick={() => handleExpireTimeChange(period.value, index + 3)}
                            >
                              {period.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Note section (no tags) */}
              <div className="note-section">
                <MessageScriptTextArea
                  title={"Note"}
                  value={notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
                  placeholder={t("write-a-note") as string}
                  showTagFooter={false}
                />
              </div>

              <div className="chat-scripts-button-container">
                <div
                  className={`${"chat-scripts-button"} ${isConfirmDisabled || loading ? "disabled" : "enabled"}`}
                  onClick={!isConfirmDisabled && !loading ? handleConfirm : undefined}
                >
                  {loading ? <Loader /> : t("confirm")}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {privateFiles.length > 0 && (
        <div className="chat-scripts-blur-overlay">
          <div className="chat-scripts-blur-overlay__content">
            <AppHeader
              title={`${t("private-media")}`}
              showBackButton={true}
              onBackClick={() => {
                if (currentFileIndex > 0) {
                  setCurrentFileIndex((prev) => prev - 1);
                } else if (privateFiles[currentFileIndex]) {
                  handleRemoveFile(privateFiles[currentFileIndex].fileId);
                }
              }}
            />
            <div className="chat-scripts-blur-overlay__preview">
              {privateFiles[currentFileIndex] &&
                (privateFiles[currentFileIndex].fileType?.includes("audio") ? (
                  <div className="audio-preview-container">
                    <img src={AudioIcon} alt="Audio" className="audio-preview-icon" />
                  </div>
                ) : (
                  <img className="chat-scripts-blur-overlay__media" src={privateFiles[currentFileIndex].fileSrc} alt="media" />
                ))}
            </div>
            <div className="chat-scripts-blur-overlay__controls">
              <div className="chat-scripts-blur-overlay__row">
                <span className="chat-scripts-blur-overlay__label">{t("want-blur-media")}</span>
                {privateFiles[currentFileIndex] && (
                  <Switch
                    checked={(() => {
                      const currentFile = files.find((f: any) => f.uid === privateFiles[currentFileIndex].fileId);
                      return currentFile?.isPrivate !== undefined ? currentFile.isPrivate : true;
                    })()}
                    onChange={(checked) => handleLockSwitchChange(checked, privateFiles[currentFileIndex])}
                    className="chat-scripts-blur-overlay__switch"
                  />
                )}
              </div>
              <div className="chat-scripts-blur-overlay__desc">{t("want-blur-media-desc")}</div>
              <div className="chat-scripts-blur-overlay__next">
                {privateFiles[currentFileIndex] && (
                  <StickyButton title={t("next")} handleClickButton={() => onNextClick(privateFiles[currentFileIndex])} backgroundGreen={true} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            zIndex: 9999,
          }}
        >
          <SpinnerComponent progress={true} progressValue={progress} message={t("stay-on-page-during-uploading") as string} />
        </div>
      )}
    </div>
  );
};

export default ChatScriptsCreateView;
